import React, { useState, useEffect } from "react"
import { Box, TextField, Button, Typography, Paper, IconButton, CircularProgress } from "@mui/material"
import { ChevronLeft } from "@mui/icons-material"
import { useNavigate, useParams } from "react-router-dom"
import api from "../../services/api"
import { API_ENDPOINTS } from "../../config/api"

export default function EditSensorType() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [formData, setFormData] = useState({
    name: "",
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchSensorType = async () => {
      try {
        const response = await api.get(API_ENDPOINTS.SENSOR_TYPE.GET_ONE(id))
        setFormData({ name: response.data.sensorType.name })
      } catch (err) {
        console.error("Error fetching sensor type:", err)
        setError("Failed to fetch sensor type. Please try again.")
      } finally {
        setLoading(false)
      }
    }

    fetchSensorType()
  }, [id])

  const handleSubmit = async (event) => {
    event.preventDefault()
    setLoading(true)
    setError(null)
    try {
      await api.put(API_ENDPOINTS.SENSOR_TYPE.UPDATE(id), formData)
      navigate("/sensor-types")
    } catch (err) {
      console.error("Error updating sensor type:", err)
      setError("Failed to update sensor type. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (event) => {
    const { name, value } = event.target
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }))
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ width: "100%", pr: 2, pt: 0, backgroundColor: "background.paper", borderRadius: 8 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton
          onClick={() => navigate("/sensor-types")}
          sx={{
            mr: 2,
            color: "text.secondary",
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <ChevronLeft />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          Edit Sensor Type
        </Typography>
      </Box>

      <Paper
        sx={{
          p: 2,
          borderRadius: 6,
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
          backgroundColor: "background.paper",
        }}
      >
        <Box component="form" onSubmit={handleSubmit}>
          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          <Box sx={{ mb: 3 }}>
            <TextField
              required
              fullWidth
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 20,
                  "& fieldset": {
                    borderColor: "#E0E0E0",
                  },
                  "&:hover fieldset": {
                    borderColor: "#C41747",
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                bgcolor: "#E31B54",
                color: "#FFFFFF",
                textTransform: "none",
                borderRadius: 18,
                px: 4,
                "&:hover": { bgcolor: "#C41747" },
              }}
            >
              {loading ? "Updating..." : "Update"}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate("/sensor-types")}
              sx={{
                color: "#F9826E",
                borderColor: "#F9826E",
                textTransform: "none",
                borderRadius: 18,
                "&:hover": {
                  color: "black",
                  borderColor: "#F7583D",
                  bgcolor: "#F9826E",
                },
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}
